import React, { useState, useEffect } from 'react';
import {
    Dashboard,
    People,
    Link,
    PointOfSale,
    ManageAccounts,
    Settings,
    QrCode,
    ChevronLeft,
    AccountBalanceWallet,
    Business,
    ExpandMore,
    ExpandLess,
    Close,
    SwapHoriz,
    UnfoldMore,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useSidebarCollapsed from '../../hooks/useSidebarCollapsed';
import { useNotification } from '../../contexts/NotificationContext';
import { hosts } from '../../config/environment';
import { fetchCompanyPos } from '../../services/posService';
import { fetchTransactions } from '../../services/transactionService';
import { fetchOrganizationUserRoles } from '../../services/userRoleService';
import {
    getOrganizationRoleKeys,
    getRoleLabel,
    normalizeRoleKey,
    organizationNeedsRoleChoice,
} from '../../utils/organizationRoleUtils';

const AdminSidebar = ({ isOpen, onToggle, activeSection }) => {
    const { organizations, selectedOrganization, selectOrganization } = useAuth();
    const { notifications } = useNotification();
    const [showOrganizationsModal, setShowOrganizationsModal] = useState(false);
    const [showRolePickerModal, setShowRolePickerModal] = useState(false);
    const [rolePickerOrg, setRolePickerOrg] = useState(null);
    const [stats, setStats] = useState({
        paymentRequests: 0,
        invoices: 0,
        transactions: 0,
        withdrawals: 0,
        clients: 0,
        caisses: 0,
        users: 0
    });
    const navigate = useNavigate();
    const location = useLocation();
    const [collapsed, toggleCollapsed] = useSidebarCollapsed('flow_admin_sidebar_collapsed');
    const [expandedGroups, setExpandedGroups] = useState({
        payments: true,
        transactions: true,
        management: true,
        analytics: true
    });

    useEffect(() => {
        loadStats();
    }, [selectedOrganization]);

    const loadStats = async () => {
        if (!selectedOrganization) return;
        
        try {
            const solutionId = localStorage.getItem('netikash_solution');

            // Charger les statistiques réelles en parallèle
            const [posList, transactionsData, userRolesData] = await Promise.all([
                fetchCompanyPos(selectedOrganization.id).catch(() => []),
                fetchTransactions({
                    own: selectedOrganization.id, // Utiliser l'ID directement
                    sol: solutionId,
                    srv: 'all',
                    act: 'all',
                    ref: 'all',
                    opr: 'all',
                    // slug: pas envoyé si vide (comme dans le mobile)
                    crc: 'all', // Toutes les devises pour le comptage
                    sts: 'all',
                    lmt: 1,
                    pge: 1
                }).catch(() => ({ contents: [] })),
                fetchOrganizationUserRoles(selectedOrganization.id, {
                    pge: 1,
                    lmt: 100,
                    usr: 'all',
                    rol: 'all',
                    sts: 'all',
                }).catch(() => ({ contents: [] }))
            ]);

            // Compter les notifications non lues
            const unreadNotifications = notifications.filter(n => !n.msgRead).length;

            // Compter les transactions par statut
            const allTransactions = await fetchTransactions({
                own: selectedOrganization.id, // Utiliser l'ID directement
                sol: solutionId,
                srv: 'all',
                act: 'all',
                ref: 'all',
                opr: 'all',
                // slug: pas envoyé si vide (comme dans le mobile)
                crc: 'all', // Toutes les devises pour le comptage
                sts: 'all',
                lmt: 100,
                pge: 1
            }).catch(() => ({ contents: [] }));

            const pendingTransactions = allTransactions.contents.filter(t => 
                t.status === 'pending' || t.status === 'PENDING'
            ).length;

            // Compter les utilisateurs uniques
            const uniqueUsers = new Set(userRolesData.contents.map(ur => ur.user?.id).filter(Boolean));
            
            // Compter les clients uniques (acheteurs dans les transactions)
            const uniqueClients = new Set(
                allTransactions.contents
                    .map(t => t.buyer?.account || t.buyer?.id)
                    .filter(Boolean)
            );

            setStats({
                paymentRequests: unreadNotifications, // Approximatif
                invoices: 0, // À implémenter si API disponible
                transactions: allTransactions.contents.length,
                withdrawals: allTransactions.contents.filter(t => 
                    t.operation === 'withdrawal' || t.operation === 'WITHDRAWAL'
                ).length,
                clients: uniqueClients.size,
                caisses: posList.length,
                users: uniqueUsers.size
            });
        } catch (error) {
            console.error('Error loading stats:', error);
        }
    };

    const getOrgLogoUrl = (orgId) => (orgId && hosts.api)
        ? `${hosts.api}/exposed/v1/mda/images/show?target=logo&target_id=${orgId}`
        : '';

    const handleOrganizationsClick = () => {
        setShowOrganizationsModal(true);
    };

    const handleConnectToOrganization = (orgId, role) => {
        const org = organizations?.find((o) => o.id === orgId);
        selectOrganization(orgId, role, org);
        setShowOrganizationsModal(false);
        setShowRolePickerModal(false);
        setRolePickerOrg(null);
        window.location.reload();
    };

    const handleOrganizationCardConnect = (org) => {
        const fullOrg = organizations?.find((o) => o.id === org.id) || org;
        if (organizationNeedsRoleChoice(fullOrg)) {
            setRolePickerOrg(fullOrg);
            setShowRolePickerModal(true);
            return;
        }
        const role = normalizeRoleKey(fullOrg.role) || 'admin';
        handleConnectToOrganization(fullOrg.id, role);
    };

    const getRoleOptionsForOrg = (org) => {
        const roles = org?.current_user_roles || org?.roles || [];
        if (roles.length > 0) {
            return roles
                .map((r) => ({
                    key: normalizeRoleKey(r),
                    label: r.name || getRoleLabel(normalizeRoleKey(r)),
                }))
                .filter((o) => o.key === 'admin' || o.key === 'cashier');
        }
        return getOrganizationRoleKeys(org).map((key) => ({
            key,
            label: getRoleLabel(key),
        }));
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'bg-green-500';
            case 'inactive': return 'bg-red-500';
            default: return 'bg-gray-500';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'active': return 'Active';
            case 'inactive': return 'Inactive';
            default: return 'Inconnu';
        }
    };

    const menuGroups = [
        {
            id: 'core',
            label: 'Principal',
            items: [
                {
                    id: 'dashboard',
                    label: 'Tableau de Bord',
                    icon: Dashboard,
                    path: '/dashboard',
                    gradient: 'from-amber-500 to-orange-500',
                    badge: null
                }
            ]
        },
        {
            id: 'payments',
            label: 'Paiements',
            items: [
                {
                    id: 'payment-requests',
                    label: 'Demandes Paiement',
                    icon: QrCode,
                    path: '/admin/payment-requests',
                    gradient: 'from-blue-500 to-cyan-500',
                    badge: stats.paymentRequests > 0 ? String(stats.paymentRequests) : null
                },
                {
                    id: 'payment-links',
                    label: 'Liens de Paiement',
                    icon: Link,
                    path: '/admin/payment-links',
                    gradient: 'from-purple-500 to-pink-500',
                    badge: null
                },
                // Factures / Articles masqués pour la mise en dev : décommenter pour réactiver
                // {
                    // id: 'invoices',
                    // label: 'Facturation',
                    // icon: Receipt,
                    // path: '/admin/invoices',
                    // gradient: 'from-orange-500 to-red-500',
                    // badge: stats.invoices > 0 ? String(stats.invoices) : null
                // }
            ]
        },
        {
            id: 'transactions',
            label: 'Transactions',
            items: [
                {
                    id: 'all-transactions',
                    label: 'Toutes les Transactions',
                    icon: SwapHoriz,
                    path: '/admin/transactions',
                    gradient: 'from-green-500 to-emerald-500',
                    badge: stats.transactions > 0 ? String(stats.transactions) : null
                },
                {
                    id: 'withdrawals',
                    label: 'Retraits',
                    icon: AccountBalanceWallet,
                    path: '/admin/WithdrawalPage',
                    gradient: 'from-amber-500 to-yellow-500',
                    badge: stats.withdrawals > 0 ? String(stats.withdrawals) : null
                }
            ]
        },
        {
            id: 'management',
            label: 'Gestion',
            items: [
                // Factures / Articles masqués pour la mise en dev : décommenter pour réactiver
                // {
                    // id: 'articles',
                    // label: 'Articles',
                    // icon: Inventory,
                    // path: '/admin/articles',
                    // gradient: 'from-purple-500 to-pink-500',
                    // badge: null
                // },
                {
                    id: 'clients',
                    label: 'Clients',
                    icon: People,
                    path: '/admin/clients',
                    gradient: 'from-teal-500 to-green-500',
                    badge: stats.clients > 0 ? String(stats.clients) : null
                },
                {
                    id: 'caisses',
                    label: 'Caisses',
                    icon: PointOfSale,
                    path: '/admin/caisses',
                    gradient: 'from-indigo-500 to-blue-500',
                    badge: stats.caisses > 0 ? String(stats.caisses) : null
                },
                {
                    id: 'users',
                    label: 'Utilisateurs',
                    icon: ManageAccounts,
                    path: '/admin/users',
                    gradient: 'from-cyan-500 to-blue-500',
                    badge: stats.users > 0 ? String(stats.users) : null
                }
            ]
        },
        {
            id: 'analytics',
            label: 'Analytique',
            items: [
                {
                    id: 'settings',
                    label: 'Paramètres',
                    icon: Settings,
                    path: '/admin/settings',
                    gradient: 'from-gray-600 to-gray-700',
                    badge: null
                }
            ]
        }
    ]
        .filter((group) => group.items.length > 0);

    const handleNavigation = (path) => {
        navigate(path);
        if (window.innerWidth < 1024) {
            onToggle();
        }
    };

    const isActive = (path) => {
        return location.pathname === path;
    };

    const toggleGroup = (groupId) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    };

    const ModalWrapper = ({ children, footer, title, subtitle, icon: Icon, onClose, size = "md" }) => (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
        >
            <div
                className={`flex w-full min-h-0 flex-col ${
                    size === 'sm'
                        ? 'max-w-md'
                        : size === 'md'
                        ? 'max-w-2xl'
                        : size === 'lg'
                        ? 'max-w-4xl'
                        : 'max-w-6xl'
                } max-h-[min(90vh,900px)]`}
            >
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-gray-700 bg-gradient-to-br from-gray-900 to-gray-800 shadow-2xl">
                    {/* En-tête fixe */}
                    <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-700 p-6">
                        <div className="flex items-center space-x-3">
                            {Icon && <Icon className="text-2xl text-amber-400" />}
                            <div>
                                <h2 id="modal-title" className="text-xl font-bold text-white">
                                    {title}
                                </h2>
                                {subtitle && <p className="text-sm text-gray-400">{subtitle}</p>}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg p-2 text-gray-400 transition-all duration-300 hover:bg-gray-700 hover:text-white"
                            aria-label="Fermer"
                        >
                            <Close className="text-xl" />
                        </button>
                    </div>

                    {/* Contenu scrollable */}
                    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                        {children}
                    </div>

                    {footer && (
                        <div className="flex-shrink-0 border-t border-gray-700 bg-gray-800/20">
                            {footer}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const OrganizationCard = ({ org, onConnect, logoUrl, roleBadges = [] }) => (
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700 hover:border-amber-500/30 transition-all duration-300 hover:bg-gray-700/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center space-x-4 flex-1">
                    <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-600">
                        {logoUrl ? (
                            <img src={logoUrl} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling?.classList.remove('hidden'); }} />
                        ) : null}
                        <span className={`text-white font-bold text-lg ${logoUrl ? 'hidden' : ''}`}>{(org.name || org.companyName || 'O').charAt(0).toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white text-lg truncate">{org.name || org.companyName || 'Organisation'}</h3>
                        <p className="text-gray-400 text-sm">
                            {(() => {
                                // Extraire le type/catégorie de manière sécurisée
                                if (typeof org.type === 'string') {
                                    return org.type;
                                }
                                if (typeof org.category === 'string') {
                                    return org.category;
                                }
                                if (org.type && typeof org.type === 'object') {
                                    return org.type.name || org.type.description || 'Organisation';
                                }
                                if (org.category && typeof org.category === 'object') {
                                    return org.category.name || org.category.description || 'Organisation';
                                }
                                return 'Organisation';
                            })()}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 mt-2">
                            <div className="flex items-center space-x-1">
                                <div className={`w-2 h-2 rounded-full ${getStatusColor(org.status)}`}></div>
                                <span className="text-xs text-gray-400">
                                    {getStatusText(org.status)}
                                </span>
                            </div>
                            {org.users !== undefined && (
                            <span className="text-xs text-gray-400">
                                {org.users} utilisateur(s)
                            </span>
                            )}
                            {org.lastActive && (
                            <span className="text-xs text-gray-400">
                                    Dernière activité: {typeof org.lastActive === 'string' ? org.lastActive : new Date(org.lastActive).toLocaleDateString('fr-FR')}
                            </span>
                            )}
                        </div>
                        {roleBadges.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                                {roleBadges.map((badge) => (
                                    <span
                                        key={badge}
                                        className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-gray-700/80 text-amber-200 border border-gray-600"
                                    >
                                        {badge}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => onConnect(org)}
                    disabled={org.status === 'inactive'}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 whitespace-nowrap ${
                        org.status === 'active'
                            ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-lg hover:shadow-amber-500/25'
                            : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    }`}
                >
                    {org.status === 'active' ? 'Se connecter' : 'Inactive'}
                </button>
            </div>
        </div>
    );

    return (
        <>
            {/* Fond sombre du tiroir mobile */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 lg:hidden"
                    onClick={onToggle}
                    aria-hidden="true"
                />
            )}

            {/* Sidebar : tiroir sur mobile, fixe et réductible (icônes seules) sur grand écran */}
            <aside
                className={`
                    fixed lg:sticky top-0 left-0 h-screen z-50 shrink-0
                    flex flex-col bg-white border-r border-slate-200 shadow-xl lg:shadow-none
                    transition-[width,transform] duration-300 ease-out
                    w-72 ${collapsed ? 'lg:w-20' : 'lg:w-72'}
                    ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
                `}
            >
                {/* Marque */}
                <div className={`flex items-center h-20 px-4 border-b border-slate-100 ${collapsed ? 'lg:justify-center lg:px-0' : 'justify-between'}`}>
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-white border border-amber-200/70 shadow-sm flex items-center justify-center overflow-hidden p-1 shrink-0">
                            <img
                                src="/images/Logo_Netikash%20Flow%20-%20Logo%20Only%20512x512.png"
                                alt="Flow"
                                className="w-full h-full object-contain"
                            />
                        </div>
                        <div className={`min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
                            <p className="text-base font-bold text-slate-900 leading-tight">Netikash Flow</p>
                            <p className="text-xs font-medium text-amber-600">Espace administrateur</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onToggle}
                        className="lg:hidden p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                        aria-label="Fermer le menu"
                    >
                        <Close fontSize="small" />
                    </button>
                </div>

                {/* Organisation active / bascule entre comptes */}
                <div className={`px-3 pt-4 ${collapsed ? 'lg:px-2' : ''}`}>
                    <button
                        type="button"
                        onClick={handleOrganizationsClick}
                        title={collapsed ? (selectedOrganization?.name || 'Mes organisations') : undefined}
                        className={`group w-full flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 hover:bg-amber-50 hover:border-amber-200 transition-colors p-2.5 ${collapsed ? 'lg:justify-center lg:p-1.5' : ''}`}
                    >
                        <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                            {selectedOrganization && getOrgLogoUrl(selectedOrganization.id) ? (
                                <img
                                    src={getOrgLogoUrl(selectedOrganization.id)}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling?.classList.remove('hidden'); }}
                                />
                            ) : null}
                            <span className={`text-amber-600 font-bold text-sm ${selectedOrganization && getOrgLogoUrl(selectedOrganization.id) ? 'hidden' : ''}`}>
                                {(selectedOrganization?.name || 'O').charAt(0).toUpperCase()}
                            </span>
                        </div>
                        <div className={`min-w-0 flex-1 text-left ${collapsed ? 'lg:hidden' : ''}`}>
                            <span className="block text-sm font-semibold text-slate-800 truncate">
                                {selectedOrganization?.name || 'Choisir un compte'}
                            </span>
                            <span className="block text-xs text-slate-500">Changer d&apos;organisation</span>
                        </div>
                        <UnfoldMore className={`text-slate-400 group-hover:text-amber-600 shrink-0 ${collapsed ? 'lg:hidden' : ''}`} fontSize="small" />
                    </button>
                </div>

                {/* Navigation */}
                <nav className={`flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 space-y-5 ${collapsed ? 'lg:px-2' : ''}`}>
                    {menuGroups.map((group) => {
                        const groupOpen = group.id === 'core' || expandedGroups[group.id];
                        return (
                            <div key={group.id}>
                                {group.id !== 'core' && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => toggleGroup(group.id)}
                                            className={`w-full flex items-center justify-between px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600 ${collapsed ? 'lg:hidden' : ''}`}
                                        >
                                            {group.label}
                                            {expandedGroups[group.id] ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                                        </button>
                                        {collapsed && <div className="hidden lg:block mx-3 mb-2 border-t border-slate-100" />}
                                    </>
                                )}

                                <div className={`space-y-1 ${groupOpen ? '' : 'hidden'} ${collapsed ? 'lg:block' : ''}`}>
                                    {group.items.map((item) => {
                                        const Icon = item.icon;
                                        const active = isActive(item.path);
                                        return (
                                            <button
                                                key={item.id}
                                                type="button"
                                                onClick={() => handleNavigation(item.path)}
                                                aria-current={active ? 'page' : undefined}
                                                className={`group relative w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                                                    collapsed ? 'lg:justify-center lg:px-0' : ''
                                                } ${
                                                    active
                                                        ? 'bg-amber-50 text-amber-800 font-semibold'
                                                        : 'text-slate-600 font-medium hover:bg-slate-50 hover:text-slate-900'
                                                }`}
                                            >
                                                {/* Repère de la page active */}
                                                {active && <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-amber-500" />}

                                                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${
                                                    active ? 'bg-amber-500 text-white shadow-sm shadow-amber-500/30' : 'text-slate-500 group-hover:text-slate-700'
                                                }`}>
                                                    <Icon sx={{ fontSize: 20 }} />
                                                </span>

                                                <span className={`flex-1 text-left truncate ${collapsed ? 'lg:hidden' : ''}`}>{item.label}</span>

                                                {item.badge && (
                                                    <span className={`min-w-[1.5rem] px-1.5 py-0.5 rounded-full text-[11px] font-semibold text-center ${
                                                        active ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600'
                                                    } ${collapsed ? 'lg:hidden' : ''}`}>
                                                        {item.badge}
                                                    </span>
                                                )}

                                                {/* Info-bulle en mode réduit */}
                                                {collapsed && (
                                                    <span className="hidden lg:block pointer-events-none absolute left-full ml-3 px-2.5 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-lg">
                                                        {item.label}
                                                        {item.badge && <span className="ml-2 px-1.5 rounded-full bg-amber-500">{item.badge}</span>}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </nav>

                {/* Réduire / agrandir (grand écran) */}
                <div className="hidden lg:block border-t border-slate-100 p-3">
                    <button
                        type="button"
                        onClick={toggleCollapsed}
                        title={collapsed ? 'Agrandir le menu' : 'Réduire le menu'}
                        className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-800 transition-colors ${collapsed ? 'justify-center px-0' : ''}`}
                    >
                        <ChevronLeft className={`transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`} fontSize="small" />
                        {!collapsed && <span>Réduire le menu</span>}
                    </button>
                </div>
            </aside>

            {/* Modal des Organisations */}
            {showOrganizationsModal && (
                <ModalWrapper
                    title="Mes Organisations"
                    subtitle="Sélectionnez une organisation pour vous connecter"
                    icon={Business}
                    onClose={() => setShowOrganizationsModal(false)}
                    size="md"
                    footer={
                        <div className="flex flex-col items-center justify-between gap-3 p-4 sm:flex-row">
                            <span className="text-center text-sm text-gray-400 sm:text-left">
                                {organizations?.filter(org => org.status === 'active' || !org.status).length || 0}{' '}
                                organisation(s) active(s) sur {organizations?.length || 0}
                            </span>
                            <button
                                type="button"
                                onClick={() => setShowOrganizationsModal(false)}
                                className="rounded-lg bg-gray-700/50 px-4 py-2 text-sm text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
                            >
                                Fermer
                            </button>
                        </div>
                    }
                >
                    <div className="p-6">
                        {/* En-tête des statistiques */}
                        <div className="mb-6 grid grid-cols-2 gap-4">
                            <div className="rounded-xl border border-gray-700 bg-gray-800/50 p-4">
                                <div className="text-sm text-amber-400">Organisations actives</div>
                                <div className="text-2xl font-bold text-white">
                                    {organizations?.filter(org => org.status === 'active' || !org.status).length || 0}
                                </div>
                            </div>
                            <div className="rounded-xl border border-gray-700 bg-gray-800/50 p-4">
                                <div className="text-sm text-gray-400">Total organisations</div>
                                <div className="text-2xl font-bold text-white">
                                    {organizations?.length || 0}
                                </div>
                            </div>
                        </div>

                        {/* Liste des Organisations (défilement dans le corps du modal) */}
                        <div className="space-y-4 pb-2">
                            {organizations?.map((org) => {
                                // S'assurer que org.type et org.category sont des strings, pas des objets
                                let orgType = 'Organisation';
                                if (typeof org.type === 'string') {
                                    orgType = org.type;
                                } else if (typeof org.category === 'string') {
                                    orgType = org.category;
                                } else if (org.type && typeof org.type === 'object') {
                                    orgType = org.type.name || org.type.description || 'Organisation';
                                } else if (org.category && typeof org.category === 'object') {
                                    orgType = org.category.name || org.category.description || 'Organisation';
                                }
                                
                                // Nettoyer l'objet org pour ne garder que les valeurs primitives
                                const cleanedOrg = {
                                    id: org.id,
                                    name: org.name || org.companyName || 'Organisation',
                                    companyName: org.companyName || org.name || 'Organisation',
                                    type: orgType,
                                    category: typeof org.category === 'string' ? org.category : orgType,
                                    status: org.status || 'active',
                                    users: org.users || 0,
                                    lastActive: org.lastActive || new Date().toISOString().split('T')[0],
                                    role: org.role || 'admin',
                                    logo: typeof org.logo === 'string' ? org.logo : '🏢',
                                    current_user_roles: org.current_user_roles || org.roles || [],
                                };

                                const roleBadges = (org.current_user_roles || []).map(
                                    (r) => r.name || getRoleLabel(normalizeRoleKey(r))
                                );
                                
                                return (
                                <OrganizationCard
                                    key={org.id}
                                    org={cleanedOrg}
                                    logoUrl={getOrgLogoUrl(org.id)}
                                    roleBadges={roleBadges}
                                    onConnect={handleOrganizationCardConnect}
                                />
                                );
                            })}
                        </div>

                        {(!organizations || organizations.length === 0) && (
                            <div className="text-center py-8">
                                <Business className="text-gray-500 text-4xl mx-auto mb-4" />
                                <h3 className="text-gray-400 text-lg font-medium">Aucune organisation</h3>
                                <p className="text-gray-500 text-sm mt-1">
                                    Vous n'avez accès à aucune organisation pour le moment.
                                </p>
                            </div>
                        )}
                    </div>
                </ModalWrapper>
            )}

            {showRolePickerModal && rolePickerOrg && (
                <ModalWrapper
                    title="Continuer en tant que"
                    subtitle={rolePickerOrg.name}
                    icon={SwapHoriz}
                    onClose={() => {
                        setShowRolePickerModal(false);
                        setRolePickerOrg(null);
                    }}
                    size="sm"
                >
                    <div className="p-6 space-y-3">
                        {getRoleOptionsForOrg(rolePickerOrg).map((option) => (
                            <button
                                key={option.key}
                                type="button"
                                onClick={() => handleConnectToOrganization(rolePickerOrg.id, option.key)}
                                className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-gray-600 bg-gray-800/50 text-white hover:border-amber-500 hover:bg-gray-700/50 transition-all"
                            >
                                <span className="font-medium">{option.label}</span>
                                <span className="text-xs text-gray-400 capitalize">{option.key}</span>
                            </button>
                        ))}
                    </div>
                </ModalWrapper>
            )}
        </>
    );
};

export default AdminSidebar;